import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Create the language context
const SimpleLanguageContext = createContext();

// Custom hook to use the language context
export const useSimpleLanguage = () => {
  const context = useContext(SimpleLanguageContext);
  if (!context) {
    throw new Error('useSimpleLanguage must be used within a SimpleLanguageProvider');
  }
  return context;
};

// Available languages
const availableLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' }
];

// Simple language provider component
export const SimpleLanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated, token } = useAuth();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Load language from localStorage or user preferences
  useEffect(() => {
    const loadLanguage = async () => {
      try {
        // If user is authenticated, load from server
        if (isAuthenticated && user && token) {
          const response = await fetch(`${API_BASE}/preferences/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const preferences = await response.json();
            if (preferences.language) {
              setCurrentLanguage(preferences.language);
              localStorage.setItem('videbits-language', preferences.language);
            }
          } else {
            // Fall back to localStorage
            const savedLanguage = localStorage.getItem('videbits-language') || 'en';
            setCurrentLanguage(savedLanguage);
          }
        } else {
          // Use localStorage for non-authenticated users
          const savedLanguage = localStorage.getItem('videbits-language') || 'en';
          setCurrentLanguage(savedLanguage);
        }
      } catch (error) {
        console.error('Error loading language:', error);
        // Fall back to default
        setCurrentLanguage('en');
      } finally {
        setLoading(false);
      }
    };

    loadLanguage();
  }, [isAuthenticated, user, token, API_BASE]);

  // Change language function
  const changeLanguage = async (languageCode) => {
    if (!availableLanguages.find(lang => lang.code === languageCode)) {
      console.error('Invalid language code:', languageCode);
      return false;
    }

    try {
      setLoading(true);
      
      // Change language immediately for better UX
      setCurrentLanguage(languageCode);
      
      // Save to localStorage first
      localStorage.setItem('videbits-language', languageCode);

      // If user is authenticated, save to server
      if (isAuthenticated && user && token) {
        const response = await fetch(`${API_BASE}/preferences/`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ language: languageCode })
        });

        if (!response.ok) {
          throw new Error('Failed to save language to server');
        }

        console.log(`Language changed to ${languageCode} and saved to server`);
      } else {
        console.log(`Language changed to ${languageCode} and saved locally`);
      }

      return true;
    } catch (error) {
      console.error('Error changing language:', error);
      // Language was already applied locally, so partial success
      return true;
    } finally {
      setLoading(false);
    }
  };

  // Get language name
  const getLanguageName = (code) => {
    const language = availableLanguages.find(lang => lang.code === code);
    return language ? language.name : code;
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return availableLanguages.find(lang => lang.code === currentLanguage) || 
           availableLanguages.find(lang => lang.code === 'en');
  };

  const value = {
    currentLanguage,
    availableLanguages,
    loading,
    changeLanguage,
    getLanguageName,
    getCurrentLanguageInfo
  };

  return (
    <SimpleLanguageContext.Provider value={value}>
      {children}
    </SimpleLanguageContext.Provider>
  );
};